import { useQuery, useLazyQuery } from "@apollo/client/react";
import { size, sortBy, uniqBy, isEmpty, filter } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";

import { pagedSearchTags, getTagDetails } from "src/queries/tags.graphql";
import { TAG_LOAD_LIMIT } from "src/util/consts";

const getUniqueTags = (tags) => {
  return sortBy(uniqBy(tags, "id"), "displayValue");
};

const getOffset = ({ initialLoad, tags, tagType }) => {
  if (initialLoad) {
    return 0;
  }

  if (!isEmpty(tags[tagType]?.tags)) {
    return size(tags[tagType]?.tags);
  }

  return 0;
};

const useLoadTags = ({ activeTagId, isSelected }) => {
  const [tagsLoading, setTagsLoading] = useState({});
  const [allTags, setAllTags] = useState({});
  const [tags, setTags] = useState({});
  const tagsRef = useRef(tags);
  tagsRef.current = tags;

  const { data: tagData } = useQuery(getTagDetails, {
    variables: {
      tagId: activeTagId,
    },
    skip: !isSelected || !activeTagId,
  });

  const [lazyLoadTags] = useLazyQuery(pagedSearchTags);

  const activeTag = (tagData?.tag || [])[0];
  const activeTagRef = useRef(activeTag);
  activeTagRef.current = activeTag;

  const loadTags = useCallback(
    (tagType, initialLoad = false) => {
      setTagsLoading((loadedTags) => ({
        ...loadedTags,
        [tagType]: true,
      }));

      const offset = getOffset({
        initialLoad,
        tags: tagsRef.current,
        tagType,
      });

      lazyLoadTags({
        variables: {
          input: {
            filter: {
              tagTypes: [tagType],
            },
            offset,
            limit: TAG_LOAD_LIMIT,
          },
          showAppliedCount: true,
        },
      })
        .then(({ data }) => {
          if (!data) {
            setTagsLoading((loadedTags) => ({
              ...loadedTags,
              [tagType]: false,
            }));
            return;
          }

          setTagsLoading((loadedTags) => ({
            ...loadedTags,
            [tagType]: false,
          }));

          const currentActiveTag = activeTagRef.current;

          setTags((prev) => ({
            ...prev,
            [tagType]: {
              tags: getUniqueTags([
                ...(prev[tagType]?.tags || []),
                ...(data?.results?.tags || []),
              ]),
              totalCount: data?.results?.totalCount,
            },
          }));

          setAllTags((prev) => ({
            ...prev,
            [tagType]: {
              tags: getUniqueTags([
                ...(prev[tagType]?.tags || []),
                ...(data?.results?.tags || []),
                ...(filter([currentActiveTag], { type: tagType }) || []),
              ]),
              totalCount: data?.results?.totalCount,
            },
          }));
        })
        .catch((err) => {
          if (err?.name === "AbortError") {
            setTagsLoading((loadedTags) => ({
              ...loadedTags,
              [tagType]: false,
            }));
            return;
          }
          throw err;
        });
    },
    [lazyLoadTags]
  );

  const loadTagsRef = useRef(loadTags);
  loadTagsRef.current = loadTags;

  useEffect(() => {
    const tagType = activeTag?.type;
    if (!tagType || !isSelected) {
      return;
    }
    if (!isEmpty(tagsRef.current[tagType]?.tags)) {
      return;
    }
    loadTagsRef.current(tagType);
  }, [activeTag?.type, isSelected]);

  return {
    tags: allTags,
    activeTag,
    loadTags,
    tagsLoading,
  };
};

export default useLoadTags;
